import { AsyncTaskLogInfoDto, LogType } from '@swagger/humanresources';
import { isFinalMilestone } from '@core/signalr';
import { PlanningTask, PlanningTaskOutcome, PlanningTaskProgress } from './planning-task.interface';

/** `12/80 Max Mustermann`: processed count, total and the contributor's name. */
const PROGRESS_LOG = /^(\d+)\/(\d+)\s+(.*)$/;
const TEXT_TYPE = 'string';

/** Progress and failure logs carry a text; the Succeeded log carries the result object. */
function isLogText(message: unknown): message is string {
  return typeof message === TEXT_TYPE;
}

/**
 * `jsonMessage` is always a JSON string: a text for progress and failures, the
 * result DTO for Succeeded. Shown raw, a text would carry its quotes (#5182).
 */
export function parseLogMessage(log: AsyncTaskLogInfoDto): unknown {
  if (log.jsonMessage == null) return null;
  try {
    return JSON.parse(log.jsonMessage);
  } catch {
    return log.jsonMessage;
  }
}

/** The progress a log reports, or null for any other log (start text, milestone). */
export function toProgress(message: unknown): PlanningTaskProgress | null {
  if (!isLogText(message)) return null;
  const match = PROGRESS_LOG.exec(message.trim());
  if (!match) return null;
  return { done: Number(match[1]), total: Number(match[2]), contributorName: match[3] };
}

/**
 * Push and GET overlap, and their `created` differ in precision, so a log is
 * recognized by its type and text, which are unique within one task (#5182).
 */
export function logKey(log: AsyncTaskLogInfoDto): string {
  return `${log.type}|${log.jsonMessage ?? ''}`;
}

/** The outcome a milestone log carries, or null for a progress log. */
export function toOutcome(log: AsyncTaskLogInfoDto, message: unknown): PlanningTaskOutcome | null {
  if (!isFinalMilestone(log.type)) return null;
  if (log.type === LogType.Succeeded) {
    return { milestone: log.type, result: message, errorText: null };
  }
  return { milestone: log.type, result: null, errorText: isLogText(message) ? message : null };
}

/** The first line of a failure text; the rest is the stack trace, which no user reads. */
export function failureSummary(errorText: string | null | undefined): string | null {
  const line = errorText?.split('\n')[0]?.trim();
  return line || null;
}

export function isRunning(task: PlanningTask): boolean {
  return task.outcome === null;
}

/** Applies logs in their order; a log seen before changes nothing. */
export function applyLogs(task: PlanningTask, logs: AsyncTaskLogInfoDto[]): PlanningTask {
  return logs.reduce(applyLog, task);
}

function applyLog(task: PlanningTask, log: AsyncTaskLogInfoDto): PlanningTask {
  const key = logKey(log);
  if (task.seenLogKeys.includes(key)) return task;
  const message = parseLogMessage(log);
  return {
    ...task,
    progress: furtherProgress(task.progress, toProgress(message)),
    outcome: task.outcome ?? toOutcome(log, message),
    lastLogCreated: laterOf(task.lastLogCreated, log.created),
    seenLogKeys: [...task.seenLogKeys, key],
  };
}

/**
 * A logs response in flight may deliver older logs after newer ones were pushed;
 * progress never moves backwards (#5182, PR 1785).
 */
function furtherProgress(
  current: PlanningTaskProgress | null,
  next: PlanningTaskProgress | null,
): PlanningTaskProgress | null {
  if (!next) return current;
  if (!current || next.done >= current.done) return next;
  return current;
}

/** The later instant; compared as dates because push and GET differ in precision (#5182). */
function laterOf(current: string | null, next: string | undefined): string | null {
  if (!next) return current;
  if (!current || Date.parse(next) >= Date.parse(current)) return next;
  return current;
}
